<script lang="ts">
  import { tweened } from "svelte/motion";
  import { circOut } from "svelte/easing";
  import { Control, setupControl } from "./controls";
  import FormField from "./FormField.svelte";
  import { Validator, type ValidatorMessages } from "./validators";
  import type { ControlValidator } from "./validators";
  import "swiped-events";
  import { getCurrentTranslations } from "../l10n/localization";

  // either set name and validators
  export let name: string = null;
  export let required: boolean = false;
  export let validators: ControlValidator[] = [];
  export let value: boolean = undefined;
  // ... or bind a control
  export let control: Control = null;
  export let standalone: boolean = false;
  export let displayName: string = undefined;
  export let errors: ValidatorMessages = undefined;

  const T = getCurrentTranslations();

  const changes = setupControl(Control, {
    name,
    validators: [...validators, ...(required ? [Validator.required] : [])],
    value,
    control,
    standalone,
  });
  $: ctrl = $changes;

  let oldValue = value;
  $: if (value !== oldValue) {
    oldValue = value;
    ctrl.value = value;
  }

  function handleClick() {
    ctrl.touch();
    ctrl.value = !ctrl.value;
  }
  function handleKeydown(e: KeyboardEvent) {
    if (e.code == "Space" || e.code == "Enter") handleClick();
  }

  let falseWidth: number;
  let trueWidth: number;
  let totalWidth: number;

  const duration = 150;
  const width = tweened(0, { duration: duration, easing: circOut });
  const left = tweened(0, { duration: duration, easing: circOut });

  $: width.set((ctrl.value ? trueWidth : falseWidth) || 0);
  $: left.set((ctrl.value ? totalWidth - trueWidth : 0) || 0);

  function swipedLeft(e: CustomEvent) {
    ctrl.value = false;
    ctrl = ctrl;
    e.preventDefault();
  }

  function swipedRight(e: CustomEvent) {
    ctrl.value = true;
    ctrl = ctrl;
    e.preventDefault();
  }

  function resizeWatcher(node: HTMLElement, handler: (node: HTMLElement) => void) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) if (entry.target === node) handler(node);
    });
    resizeObserver.observe(node);
    return {
      destroy() {
        resizeObserver.unobserve(node);
      },
    };
  }
</script>

<FormField bind:control={ctrl} {displayName} {errors} {standalone} on:change>
  <div class="input-switch-control">
    <span>
      <slot />
    </span>
    <div use:resizeWatcher={(e) => (totalWidth = e.clientWidth)} class="input-switch" on:swiped-left={swipedLeft} on:swiped-right={swipedRight} class:switch-on={ctrl.value} style:--width={$width + "px"} style:--left={$left + "px"} class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} on:click={handleClick} on:keydown={(e) => handleKeydown(e)} tabindex="0">
      <div use:resizeWatcher={(e) => (falseWidth = e.clientWidth)} class:active={!ctrl.value}>{T.get("switch.no")}</div>
      <div use:resizeWatcher={(e) => (trueWidth = e.clientWidth)} class:active={ctrl.value}>{T.get("switch.yes")}</div>
    </div>
  </div>
</FormField>

<style lang="scss" global>
  .input-switch-control {
    display: flex;
    justify-content: space-between;
    align-items: center;
    span {
      margin-right: 1em;
    }
  }

  .input-switch {
    position: relative;
    display: inline-flex;
    padding: 0;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #000000;
    background-color: #fff;
    background-clip: padding-box;
    border: 2px solid #d9d9d9;
    border-radius: 999px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    box-sizing: border-box;
    cursor: pointer;
    user-select: none;
    transform: perspective(0);

    &:focus {
      // border-color: #86b7fe;
      // outline: 0;
      // box-shadow: 0 0 0 0.25rem rgb(13 110 253 / 25%);
      border-color: #000;
    }

    > div {
      position: relative;
      padding: 0.375rem 1.1rem;
      border-radius: 999px;

      + div {
        margin-left: -0.5rem;
      }
    }

    &:before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: var(--width);
      transform: translate3d(var(--left), 0, 0);
      background-color: #ffeebf;
      // outline: solid 2px #ffbc00;
      border-radius: 999px;
      box-shadow: 0 0 2px 2px #ffbc00;
    }

    &.input-error {
      border-color: #f5c6cb;
      background-color: #f8d7da;
    }
  }
</style>
